/**
 * Query hook for the Table tab of the League Detail screen.
 * Returns normalized standings + a short-circuit `isEmpty` flag so the parent
 * can hide the Table tab when the league has no standings snapshot yet.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLeagueStandings } from '../helpers/leagueDetailApi';
import {
  leagueStandingsHasHomeAwaySplit,
  normalizeStandingsPayload,
} from '../helpers/mapLeagueStandings.helper';
import type { LeagueStandingsScope, NormalizedStandings } from '../types/leagueDetail.types';

const STALE_TIME_MS = 5 * 60 * 1000;

export interface UseLeagueStandingsOptions {
  enabled?: boolean;
  scope?: LeagueStandingsScope;
}

export interface UseLeagueStandingsResult {
  data: NormalizedStandings | null;
  /** Raw API payload (for split detection); same as query cache shape. */
  hasHomeAwaySplit: boolean;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
}

export function useLeagueStandings(
  leagueId: string | undefined,
  options?: UseLeagueStandingsOptions
): UseLeagueStandingsResult {
  const { enabled = true, scope = 'all' } = options ?? {};

  const canFetch = enabled && typeof leagueId === 'string' && leagueId.length > 0;
  const query = useQuery({
    queryKey: ['leagueDetail', 'standings', leagueId],
    queryFn: () => fetchLeagueStandings(leagueId ?? ''),
    enabled: canFetch,
    staleTime: STALE_TIME_MS,
  });

  const normalized = useMemo(
    () => normalizeStandingsPayload(query.data ?? null, scope),
    [query.data, scope]
  );

  const hasHomeAwaySplit = useMemo(
    () => leagueStandingsHasHomeAwaySplit(query.data ?? null),
    [query.data]
  );

  return {
    data: normalized,
    hasHomeAwaySplit,
    isLoading: query.isLoading,
    isError: query.isError,
    isEmpty: !query.isLoading && normalized == null,
  };
}
